import { AxiosResponse } from 'axios';
import apiClient from './apiClient';

// Discount plan types
export interface DiscountPlan {
  id: number;
  name: string;
  description: string;
  minSessions: number;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiscountPlanData {
  name: string;
  description: string;
  minSessions: number;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  isActive?: boolean;
}

export interface UpdateDiscountPlanData {
  name?: string;
  description?: string;
  minSessions?: number;
  discountType?: 'fixed' | 'percentage';
  discountValue?: number;
  isActive?: boolean;
}

export interface PriceDetails {
  totalPrice: number;
  discountAmount: number;
  finalPrice: number;
}

// Get all discount plans
export const getAllDiscountPlans = async (activeOnly = false): Promise<DiscountPlan[]> => {
  try {
    let url = '/api/discounts';
    if (activeOnly) {
      url = '/api/discounts/active';
    }
    
    const response: AxiosResponse<DiscountPlan[]> = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching discount plans:', error);
    throw error;
  }
};

// Get discount plan by ID
export const getDiscountPlanById = async (planId: number): Promise<DiscountPlan> => {
  try {
    const response: AxiosResponse<DiscountPlan> = await apiClient.get(`/api/discounts/${planId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching discount plan ${planId}:`, error);
    throw error;
  }
};

// Create a new discount plan
export const createDiscountPlan = async (planData: CreateDiscountPlanData): Promise<DiscountPlan> => {
  try {
    const response: AxiosResponse<DiscountPlan> = await apiClient.post('/api/discounts', planData);
    return response.data;
  } catch (error) {
    console.error('Error creating discount plan:', error);
    throw error;
  }
};

// Update a discount plan
export const updateDiscountPlan = async (planId: number, planData: UpdateDiscountPlanData): Promise<DiscountPlan> => {
  try {
    const response: AxiosResponse<DiscountPlan> = await apiClient.put(`/api/discounts/${planId}`, planData);
    return response.data;
  } catch (error) {
    console.error(`Error updating discount plan ${planId}:`, error);
    throw error;
  }
};

// Delete a discount plan
export const deleteDiscountPlan = async (planId: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/discounts/${planId}`);
  } catch (error) {
    console.error(`Error deleting discount plan ${planId}:`, error);
    throw error;
  }
};

// Calculate discount for sessions
export const calculateDiscount = async (sessionIds: number[], discountCode?: string): Promise<PriceDetails> => {
  try {
    const response: AxiosResponse<PriceDetails> = await apiClient.post('/api/discounts/calculate', { 
      sessionIds,
      discountCode: discountCode || undefined
    });
    return response.data;
  } catch (error) {
    console.error('Error calculating discount:', error);
    throw error;
  }
};

// Get pricing management dashboard data (admin only)
export const getPricingManagementDashboard = async () => {
  try {
    const response = await apiClient.get('/api/discounts/pricing-management/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching pricing management dashboard:', error);
    throw error;
  }
};